"use client";

import { useState, useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { MAX_RATING_TEXT_LENGTH } from "@/lib/constants";

export default function RatePage() {
  const [score, setScore] = useState(0);
  const [hoverScore, setHoverScore] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherName, setOtherName] = useState<string>("");
  const [alreadyRated, setAlreadyRated] = useState(false);

  const router = useRouter();
  const params = useParams();
  const matchId = params.matchId as string;
  const supabase = createClient();

  useEffect(() => {
    async function loadMatch() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push("/auth/login"); return; }

      // Get match details
      const { data: match } = await supabase
        .from("matches")
        .select("id, worker_id, status, jobs(company_id, title)")
        .eq("id", matchId)
        .single();

      if (!match || match.status !== "hired") {
        setError("This match is not available for rating.");
        setLoading(false);
        return;
      }

      // Check if already rated
      const { data: existingRating } = await supabase
        .from("ratings")
        .select("id")
        .eq("match_id", matchId)
        .eq("from_id", user.id)
        .single();

      if (existingRating) {
        setAlreadyRated(true);
        setLoading(false);
        return;
      }

      // Get the other person's name
      const isWorker = match.worker_id === user.id;
      const jobs = match.jobs as unknown as { company_id: string; title: string } | null;

      if (isWorker && jobs?.company_id) {
        const { data: company } = await supabase
          .from("companies")
          .select("name, profile_id")
          .eq("id", jobs.company_id)
          .single();
        setOtherName(company?.name || "the contractor");
      } else if (!isWorker) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("name")
          .eq("id", match.worker_id)
          .single();
        setOtherName(profile?.name || "the worker");
      } else {
        setOtherName("the other party");
      }

      setLoading(false);
    }

    loadMatch();
  }, [matchId, supabase, router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (score === 0) { setError("Please select a rating."); return; }

    setSubmitting(true);
    setError(null);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get the other person's ID
    const { data: match } = await supabase
      .from("matches")
      .select("worker_id, jobs(company_id)")
      .eq("id", matchId)
      .single();

    if (!match) { setError("Match not found."); setSubmitting(false); return; }

    const isWorker = match.worker_id === user.id;
    let toId: string;

    if (isWorker) {
      // Worker rating the contractor — need to find the contractor's profile_id
      const jobs = match.jobs as unknown as { company_id: string } | null;
      if (!jobs?.company_id) { setError("Could not find contractor."); setSubmitting(false); return; }
      const { data: company } = await supabase.from("companies").select("profile_id").eq("id", jobs.company_id).single();
      if (!company) { setError("Could not find contractor."); setSubmitting(false); return; }
      toId = company.profile_id;
    } else {
      toId = match.worker_id;
    }

    const { error: insertError } = await supabase.from("ratings").insert({
      match_id: matchId,
      from_id: user.id,
      to_id: toId,
      score,
      comment: comment.trim() || null,
    });

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    // Record success milestones (fire-and-forget)
    fetch('/api/success-tracking/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ milestone_type: 'first_rating', metadata: { match_id: matchId, score } }),
    }).catch(() => {});

    if (score === 5) {
      fetch('/api/success-tracking/record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ milestone_type: 'five_star_given', metadata: { match_id: matchId } }),
      }).catch(() => {});
    }

    setSuccess(true);
    setSubmitting(false);

    setTimeout(() => {
      router.push("/dashboard");
      router.refresh();
    }, 2000);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (alreadyRated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Already rated</CardTitle>
            <CardDescription className="mt-2">
              You&apos;ve already submitted a rating for this job.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full h-12 text-base" variant="outline">
              <Link href="/dashboard">Back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Thanks for rating!</CardTitle>
            <CardDescription className="mt-2">
              Your feedback helps build trust on RateRight. Redirecting...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Rate your experience</CardTitle>
          <CardDescription className="mt-2">
            How was working with {otherName}?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Star Rating */}
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScore(value)}
                  onMouseEnter={() => setHoverScore(value)}
                  onMouseLeave={() => setHoverScore(0)}
                  className="p-1 transition-transform active:scale-90"
                  aria-label={`${value} star${value !== 1 ? "s" : ""}`}
                >
                  <Star
                    className={cn(
                      "h-10 w-10 transition-colors",
                      (hoverScore || score) >= value
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground/30"
                    )}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {score === 0 && "Tap a star to rate"}
              {score === 1 && "Poor"}
              {score === 2 && "Below average"}
              {score === 3 && "Average"}
              {score === 4 && "Good"}
              {score === 5 && "Excellent"}
            </p>

            {/* Comment */}
            <div className="space-y-2">
              <label htmlFor="comment" className="text-sm font-medium">
                Comment <span className="text-muted-foreground">(optional)</span>
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="How was the experience?"
                rows={3}
                maxLength={MAX_RATING_TEXT_LENGTH}
                className="w-full rounded-lg border bg-background px-3 py-2 text-base resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3">
                <p className="text-sm text-destructive font-medium">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold"
              disabled={submitting || score === 0}
            >
              {submitting ? "Submitting..." : "Submit Rating"}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            <Link href="/dashboard" className="text-primary underline hover:no-underline">
              Skip for now
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
